/**
 * 기능별 예시 결과물 미리보기 모달.
 *
 * 백엔드 GET /api/preview-examples 가 feature key 별 [{filename,label,type,url}] 반환.
 * - PDF: <iframe src="…#zoom=page-fit"> 으로 인라인 렌더 (브라우저 PDF 뷰어 활용)
 * - MP4: <video controls> 로 인라인 재생
 * - 여러 문서가 있으면 좌측 탭으로 전환
 */
import { lazy, Suspense, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { FiX, FiExternalLink, FiPlayCircle, FiFileText, FiImage } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { api } from '../../lib/api'
import { cn } from '../../lib/utils'
import Spinner from '../ui/Spinner'

const PdfViewer = lazy(() => import('./PdfViewer'))

interface ExampleItem {
  filename: string
  label: string
  type: 'pdf' | 'video' | 'image'
  url: string
  size_bytes?: number
}

interface Props {
  featureKey: string  // 'jsa' | 'work_permit' | 'safety_education' | ...
  isOpen: boolean
  onClose: () => void
}

export default function ExamplePreviewModal({ featureKey, isOpen, onClose }: Props) {
  const { t } = useTranslation()
  const [items, setItems] = useState<ExampleItem[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeIdx, setActiveIdx] = useState(0)

  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    setError(null)
    setActiveIdx(0)
    api.get<Record<string, ExampleItem[]>>('/preview-examples')
      .then((r) => {
        const list = r.data[featureKey] ?? []
        // 영상 파일을 항상 첫 번째로 정렬 — 임팩트 있는 결과물을 모달 열자마자 보여주기 위함.
        // 백엔드 catalog 순서가 갱신 안 된 환경에서도 frontend 가 보장.
        const sorted = [...list].sort((a, b) => {
          if (a.type === 'video' && b.type !== 'video') return -1
          if (a.type !== 'video' && b.type === 'video') return 1
          return 0
        })
        setItems(sorted)
        setActiveIdx(0)
        if (sorted.length === 0) setError(t('commonComponents.examplePreviewModal.noExamples', { defaultValue: '이 기능에 등록된 예시가 없습니다.' }))
      })
      .catch(() => setError(t('commonComponents.examplePreviewModal.loadFailed', { defaultValue: '예시 결과물을 불러오지 못했습니다.' })))
      .finally(() => setLoading(false))
  }, [isOpen, featureKey, t])

  if (!isOpen) return null

  // axios baseURL 은 production "/api". 절대 URL 로 만들기
  const baseUrl = (api.defaults.baseURL ?? '').replace(/\/$/, '')
  const fullUrl = (item: ExampleItem) => {
    // 브라우저/pdf.js 캐시 회피 — size_bytes 가 바뀌면 URL 도 바뀌어 강제 재다운로드.
    // 정적 파일이라도 서버에서 교체되면 즉시 반영됨.
    const cacheBuster = item.size_bytes ? `?v=${item.size_bytes}` : ''
    if (item.url.startsWith('http')) return item.url + cacheBuster
    if (baseUrl.startsWith('http')) {
      try {
        const u = new URL(baseUrl)
        return `${u.origin}${item.url}${cacheBuster}`
      } catch { return item.url + cacheBuster }
    }
    return item.url + cacheBuster
  }

  const active = items?.[activeIdx] ?? null
  const activeUrl = active ? fullUrl(active) : ''

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          'relative w-full max-w-6xl h-[90vh]',
          'bg-surface-card border border-surface-elevated/60 rounded-lg shadow-md overflow-hidden flex flex-col',
        )}
      >
        {/* 헤더 */}
        <div className="px-5 py-3 border-b border-surface-elevated/40 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-sm font-bold text-ink">
              {t('commonComponents.examplePreviewModal.title', { defaultValue: '📂 예시 결과물' })}
            </h2>
            <p className="text-[11px] text-ink-faint mt-0.5">
              {t('commonComponents.examplePreviewModal.subtitle', { defaultValue: '실제 생성 결과를 미리 확인해보세요. 본인 데이터로 생성하면 입력 내용에 맞춰 새로 만들어집니다.' })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-ink-faint hover:text-ink hover:bg-surface-elevated/50 rounded-lg"
            aria-label={t('commonComponents.examplePreviewModal.close', { defaultValue: '닫기' })}
          >
            <FiX size={18} />
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 flex min-h-0">
          {/* 좌: 목록 (여러 문서 있을 때만 노출) */}
          {items && items.length > 1 && (
            <aside className="w-56 shrink-0 border-r border-surface-elevated/40 overflow-y-auto p-2 space-y-1">
              {items.map((it, i) => (
                <button
                  key={it.filename}
                  onClick={() => setActiveIdx(i)}
                  className={cn(
                    'w-full text-left px-3 py-2 rounded-lg text-xs transition flex items-center gap-2',
                    i === activeIdx
                      ? 'bg-brand-primary/15 text-zinc-900 dark:text-white'
                      : 'text-ink-muted hover:bg-surface-elevated/40 hover:text-ink',
                  )}
                >
                  {it.type === 'video' ? <FiPlayCircle size={14} />
                    : it.type === 'image' ? <FiImage size={14} />
                    : <FiFileText size={14} />}
                  <span className="truncate">{it.label}</span>
                </button>
              ))}
            </aside>
          )}

          {/* 우: 미리보기 영역 */}
          <div className="flex-1 min-w-0 flex flex-col bg-zinc-950/50">
            {loading && (
              <div className="flex-1 flex items-center justify-center">
                <Spinner size="lg" />
              </div>
            )}
            {!loading && error && (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-ink-faint p-8">
                <FiFileText size={48} className="opacity-30 mb-4" />
                <p>{error}</p>
              </div>
            )}
            {!loading && !error && active && (
              <>
                <div className="px-4 py-2 border-b border-surface-elevated/40 flex items-center justify-between">
                  <div className="text-xs text-ink-muted truncate">{active.label}</div>
                  <a
                    href={activeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-brand-primary-light hover:text-brand-primary"
                    title={t('commonComponents.examplePreviewModal.openInNewWindowTitle', { defaultValue: '새 창에서 열기' })}
                  >
                    <FiExternalLink size={12} />{' '}
                    {t('commonComponents.examplePreviewModal.newWindow', { defaultValue: '새 창' })}
                  </a>
                </div>
                <div className="flex-1 min-h-0">
                  {active.type === 'pdf' ? (
                    <Suspense
                      fallback={
                        <div className="w-full h-full flex items-center justify-center">
                          <Spinner size="lg" />
                        </div>
                      }
                    >
                      <PdfViewer key={activeUrl} url={activeUrl} />
                    </Suspense>
                  ) : active.type === 'image' ? (
                    <div className="w-full h-full overflow-auto bg-black/40 p-4 flex items-center justify-center">
                      <img
                        key={activeUrl}
                        src={activeUrl}
                        alt={active.label}
                        className="max-w-full h-auto rounded-md shadow-lg"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-black p-4">
                      <video
                        key={activeUrl}
                        src={activeUrl}
                        controls
                        preload="metadata"
                        className="max-w-full max-h-full"
                      >
                        {t('commonComponents.examplePreviewModal.videoUnavailable', { defaultValue: '영상을 재생할 수 없습니다.' })}
                      </video>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  )
}
